import { useNavigate } from "@tanstack/react-router";
import { useRole } from "../../components/AppShell";
import { useConfirm, useToast } from "../../components/ui";
import { useAiStatus, useLumiConfig, useSetAiEnabled } from "../../lib/queries";
import type { LumiBalance } from "../../lib/types";

/**
 * The "IA activa" switch. On: the IA answers new WhatsApp messages and hands to a person
 * what it cannot or should not answer. Off: the team answers everything.
 */
export function AiCard() {
  const role = useRole();
  const canSwitch = role === "owner" || role === "admin";
  const status = useAiStatus();
  const setEnabled = useSetAiEnabled();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const setupDone = !!useLumiConfig(canSwitch).data?.setupCompletedAt;
  const enabled = status.data?.enabled ?? false;

  async function toggle() {
    // Lumi never answers real customers with an empty setup.
    if (!enabled && !setupDone) {
      const go = await confirm({
        title: "Antes de encender Lumi, cuéntale de tu negocio",
        message:
          "Son unas pocas preguntas (unos 2 minutos). Sin eso, Lumi no sabe qué vende la empresa ni cómo hablar.",
        confirmLabel: "Configurar ahora",
      });
      if (go) navigate({ to: "/bienvenida" });
      return;
    }
    setEnabled.mutate(!enabled, {
      onSuccess: (data) => toast(data.enabled ? "IA activada" : "IA apagada"),
      onError: () => toast("No se pudo cambiar. Intenta de nuevo."),
    });
  }

  return (
    <div className="card">
      <div className="card-body ai-card">
        <div>
          <h3>IA en WhatsApp</h3>
          <p className="muted">
            {enabled
              ? "La IA contesta los mensajes nuevos y pasa a un asesor lo que no sabe, lo que pide una persona o una compra. Las conversaciones que ya atiende el equipo no las toca."
              : "Apagada: el equipo contesta todos los mensajes. Al activarla, la IA atiende primero y pasa a un asesor cuando hace falta."}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="IA activa"
          className="switch"
          disabled={!canSwitch || status.isPending || setEnabled.isPending}
          title={canSwitch ? undefined : "Solo propietarios y administradores pueden cambiarlo"}
          onClick={toggle}
        >
          <span className="switch-thumb" />
        </button>
      </div>
      {canSwitch && status.data && <LumiMessages balance={status.data.balance} />}
    </div>
  );
}

const count = new Intl.NumberFormat("es-CR");
const day = new Intl.DateTimeFormat("es-CR", { day: "numeric", month: "long", timeZone: "America/Costa_Rica" });
const date = (iso: string) => day.format(new Date(iso));

/**
 * Lumi's messages left: the month's (with a meter) and the top-ups. Owners and admins
 * only; every reply Lumi sends spends one, and at zero it hands chats to the team.
 */
function LumiMessages({ balance }: { balance: LumiBalance | null }) {
  const plan = balance?.plan;
  if (!balance || !plan) {
    return (
      <div className="card-body lumi-balance">
        <p className="muted">La prueba empieza al activar o probar Lumi: 300 mensajes durante 14 días.</p>
      </div>
    );
  }
  const share = plan.monthlyMessages ? Math.min(1, balance.monthUsed / plan.monthlyMessages) : 1;
  const level = balance.total <= 0 ? "out" : share >= 0.8 ? "low" : "ok";
  const trial = plan.name === "Prueba";
  return (
    <div className="card-body lumi-balance" data-level={level}>
      <div className="lumi-balance-head">
        <span className="lumi-balance-title">Mensajes de Lumi</span>
        <span className={`pill ${trial ? "pill-warn" : "pill-human"}`}>{trial ? "Prueba" : `Plan ${plan.name}`}</span>
      </div>
      <p className="lumi-balance-total">
        <strong>{count.format(Math.max(0, balance.total))}</strong> disponibles
      </p>
      {plan.active && (
        // The line below says the same in words; the bar is only for the eye.
        <div className="lumi-meter" aria-hidden="true">
          <span style={{ transform: `scaleX(${share})` }} />
        </div>
      )}
      <ul className="lumi-balance-lines muted">
        {plan.active ? (
          <li>
            {trial ? "En la prueba" : "Este mes"}: {count.format(balance.monthUsed)} de{" "}
            {count.format(plan.monthlyMessages)} usados
            {balance.renewsAt
              ? `, se renuevan el ${date(balance.renewsAt)}`
              : `, ${trial ? "la prueba termina" : "el plan termina"} el ${date(plan.endsAt)}`}
          </li>
        ) : (
          <li>
            {new Date(plan.startsAt) > new Date()
              ? `El plan empieza el ${date(plan.startsAt)}`
              : `El plan terminó el ${date(plan.endsAt)}`}
          </li>
        )}
        {balance.topupLeft > 0 && (
          <li>Recargas: {count.format(balance.topupLeft)} mensajes, se usan después de los del mes</li>
        )}
      </ul>
      {level === "out" && (
        <p className="lumi-balance-alert">Sin mensajes: Lumi pasa cada conversación nueva a un asesor del equipo.</p>
      )}
    </div>
  );
}
